import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TopBar } from './components/common/TopBar';
import { BottomNavValidation, VisionValidation } from './components/common/VisionValidation';
import { StatusBarService } from './services/StatusBarService';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { PublicRoute, AuthRoute } from './components/common/ProtectedRoute';
import { useVisionCheck } from './hooks/useVisionCheck';
import { Loader2 } from 'lucide-react';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const RecoverPasswordPage = lazy(() => import('./pages/auth/RecoverPasswordPage').then(m => ({ default: m.RecoverPasswordPage })));
const ValidateCodePage = lazy(() => import('./pages/auth/ValidateCodePage').then(m => ({ default: m.ValidateCodePage })));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage').then(m => ({ default: m.RegisterPage })));
const OnboardingPage = lazy(() => import('./pages/onboarding/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const ChatOnboardingPage = lazy(() => import('./pages/onboarding/ChatOnboardingPage').then(m => ({ default: m.ChatOnboardingPage })));
const HomePage = lazy(() => import('./pages/home/HomePage').then(m => ({ default: m.HomePage })));
const BooksPage = lazy(() => import('./pages/books/BooksPage').then(m => ({ default: m.BooksPage })));
const BooksSessionChat = lazy(() => import('./pages/books/BooksSessionChat').then(m => ({ default: m.BooksSessionChat })));
const AudioPlayerPage = lazy(() => import('./pages/player/AudioPlayerPage').then(m => ({ default: m.AudioPlayerPage })));
const VisualizationPage = lazy(() => import('./pages/visualization/VisualizationPage').then(m => ({ default: m.VisualizationPage })));
const VisualizationSessionChat = lazy(() => import('./pages/visualization/VisualizationSessionChat').then(m => ({ default: m.VisualizationSessionChat })));
const OnboardingSummaryPage = lazy(() => import('./pages/onboarding/OnboardingSummaryPage').then(m => ({ default: m.OnboardingSummaryPage })));
// Lazy load admin pages
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminPrompts = lazy(() => import('./pages/admin/AdminPrompts').then(m => ({ default: m.AdminPrompts })));
const AdminAIConfig = lazy(() => import('./pages/admin/AdminAIConfig'));
const AdminTTSConfig = lazy(() => import('./pages/admin/AdminTTSConfig'));
const AdminErrors = lazy(() => import('./pages/admin/AdminErrors'));
const AdminAudioAssets = lazy(() => import('./pages/admin/AdminAudioAssets'));

// Component to handle root route redirects based on authentication status
const RootRedirect = () => {
  const { isAuthenticated, isInitialized } = useAuth();
  const { hasVision, isChecking } = useVisionCheck();

  // Show loading screen until auth is initialized
  if (!isInitialized) {
    return (
      <div className="h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-[#5CD2F4] animate-spin mx-auto" />
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading screen while checking vision
  if (isAuthenticated && isChecking) {
    return (
      <div className="h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-[#5CD2F4] animate-spin mx-auto" />
          <p className="text-white text-lg">Checking your vision...</p>
        </div>
      </div>
    );
  }

  // Redirect based on authentication status and vision
  if (isAuthenticated) {
    // If user has no vision, redirect to onboarding
    if (hasVision === false) {
      return <Navigate to="/onboarding" replace />;
    }
    // If user has vision, redirect to home
    return <Navigate to="/home" replace />;
  } else {
    return <Navigate to="/login" replace />;
  }
};

// Loading component for lazy-loaded routes
const PageLoader = () => (
  <div className="h-screen bg-[#1a1a1a] flex items-center justify-center">
    <div className="text-center space-y-4">
      <Loader2 className="w-12 h-12 text-[#5CD2F4] animate-spin mx-auto" />
      <p className="text-white text-lg">Loading...</p>
    </div>
  </div>
);

const AppRoutes = () => {
  
  return (
    <div className="h-screen bg-[#1a1a1a] text-white">
      
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes - Only accessible when not authenticated */}
            <Route path="/login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } />
            <Route path="/recover-password" element={
              <PublicRoute>
                <RecoverPasswordPage />
              </PublicRoute>
            } />
            <Route path="/validate-code" element={
              <PublicRoute>
                <ValidateCodePage />
              </PublicRoute>
            } />
            <Route path="/reset-password" element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            } />

            {/* Protected Routes - Only accessible when authenticated */}
            <Route path="/onboarding" element={
              <AuthRoute>
                <TopBar />
                <OnboardingPage />
              </AuthRoute>
            } />
            <Route path="/chat-onboarding" element={
              <AuthRoute>
                <TopBar />
                <ChatOnboardingPage />
                <BottomNavValidation />
              </AuthRoute>
            } />
            <Route path="/home" element={
              <AuthRoute>
                <VisionValidation>
                  <TopBar />
                  <HomePage />
                  <BottomNavValidation />
                </VisionValidation>
              </AuthRoute>
            } />
            <Route path="/visualization" element={
              <AuthRoute>
                <VisionValidation>
                  <TopBar />
                  <VisualizationPage />
                  <BottomNavValidation />
                </VisionValidation>
              </AuthRoute>
            } />
            <Route path="/visualization/session" element={
              <AuthRoute>
                <VisionValidation>
                  <TopBar />
                  <VisualizationSessionChat />
                  <BottomNavValidation />
                </VisionValidation>
              </AuthRoute>
            } />
            <Route path="/books" element={
              <AuthRoute>
                <VisionValidation>
                  <TopBar />
                  <BooksPage />
                  <BottomNavValidation />
                </VisionValidation>
              </AuthRoute>
            } />
            <Route path="/books/session" element={
              <AuthRoute>
                <VisionValidation>
                  <TopBar />
                  <BooksSessionChat />
                  <BottomNavValidation />
                </VisionValidation>
              </AuthRoute>
            } />
            <Route path="/player" element={
              <AuthRoute>
                <VisionValidation>
                  <TopBar />
                  <AudioPlayerPage />
                  <BottomNavValidation />
                </VisionValidation>
              </AuthRoute>
            } />
            {/* /loading-audios removed — navigation goes directly from Chat to Player */}
            <Route path="/onboarding-summary" element={
              <AuthRoute>
                <VisionValidation>
                  <TopBar />
                  <OnboardingSummaryPage />
                  <BottomNavValidation />
                </VisionValidation>
              </AuthRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin" element={
              <AuthRoute>
                <AdminLayout />
              </AuthRoute>
            }>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="prompts" element={<AdminPrompts />} />
              <Route path="ai-config" element={<AdminAIConfig />} />
              <Route path="tts-config" element={<AdminTTSConfig />} />
              <Route path="audio-assets" element={<AdminAudioAssets />} />
              <Route path="errors" element={<AdminErrors />} />
            </Route>

            {/* Redirect /profile to home (profile is now a modal) */}
            <Route path="/profile" element={<Navigate to="/home" replace />} />

            {/* Default redirect - check auth status */}
            <Route path="/" element={<RootRedirect />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};


function App() {
  useEffect(() => {
    // Initialize status bar when app starts (after splash screen)
    const initializeStatusBar = async () => {
      // Wait a bit to ensure splash screen is done
      setTimeout(async () => {
        await StatusBarService.showForApp();
      }, 100);
    };

    initializeStatusBar();
  }, []);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;